import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MessageSquare, Calendar, FileText, Plus, Play, ArrowDownLeft, ArrowUpRight, Eye, MousePointerClick, Globe } from 'lucide-react';
import { AddCommunicationDialog } from './AddCommunicationDialog';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useCustomerUnifiedCommunications } from '@/hooks/useCustomerUnifiedCommunications';
import { Skeleton } from '@/components/ui/skeleton';
import type { UnifiedCommunication } from '@/types/customerCommunications';
import { MessageStatus } from '@/components/communications/MessageStatus';

interface CustomerCommunicationsProps {
  customerId: string;
  communications?: any[]; // Keep for backwards compatibility but we'll use the unified hook
}

export function CustomerCommunications({ customerId }: CustomerCommunicationsProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { formatDate } = useTimezoneUtils();
  const { data: communications = [], isLoading } = useCustomerUnifiedCommunications(customerId);

  const getIcon = (type: string) => {
    const icons: Record<string, any> = {
      call: Phone,
      email: Mail,
      sms: MessageSquare,
      meeting: Calendar,
      note: FileText,
      portal: Globe,
    };
    const Icon = icons[type] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  const getSourceBadge = (source: string) => {
    const sourceLabels: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
      customer_communications: { label: 'Manual', variant: 'outline' },
      sms_messages: { label: 'SMS', variant: 'default' },
      email_messages: { label: 'Email', variant: 'secondary' },
      call_logs: { label: 'Call', variant: 'default' },
    };
    const config = sourceLabels[source] || { label: source, variant: 'outline' as const };
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>;
  };

  const getDirectionIcon = (direction: string | null) => {
    if (direction === 'inbound') {
      return <ArrowDownLeft className="h-3 w-3 text-accent-foreground" />;
    }
    if (direction === 'outbound') {
      return <ArrowUpRight className="h-3 w-3 text-primary" />;
    }
    return null;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /* New Helper for Call Outcome Badges */
  const getCallOutcomeBadge = (comm: UnifiedCommunication) => {
    if (comm.type !== 'call') return getSourceBadge(comm.source);

    const status = comm.status || 'completed';
    const talkTime = comm.talk_time_seconds || 0;

    // Logic: If status is completed AND talk time > 0 -> Answered
    const isAnswered = status === 'completed' && talkTime > 0;
    const isVoicemail = status === 'voicemail' || (status === 'completed' && talkTime === 0);
    const isMissed = status === 'no-answer' || status === 'busy' || status === 'failed';

    if (isAnswered) {
      return (
        <Badge variant="success">
          <Phone className="h-3 w-3 mr-1" /> Answered
        </Badge>
      );
    }
    if (isMissed) {
      return (
        <Badge variant="destructive-soft">
          <ArrowDownLeft className="h-3 w-3 mr-1" /> Missed
        </Badge>
      );
    }
    if (isVoicemail) {
      return (
        <Badge variant="warning">
          <MessageSquare className="h-3 w-3 mr-1" /> Voicemail
        </Badge>
      );
    }

    // Default fallback
    return <Badge variant="outline">{status}</Badge>;
  };

  /* Helper for User/Agent Attribution */
  const getAgentDisplay = (comm: UnifiedCommunication) => {
    if (!comm.agent_name) return null;

    // Get Initials
    const initials = comm.agent_name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2 pt-2 border-t">
        <div className="h-5 w-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
          {initials}
        </div>
        <span>
          {comm.direction === 'outbound' ? 'Outbound call by ' : 'Answered by '}
          <span className="font-medium text-foreground">{comm.agent_name}</span>
        </span>
      </div>
    );
  };

  const renderCommunicationCard = (comm: UnifiedCommunication) => {
    return (
      <Card key={comm.id} className="hover:shadow-md transition-shadow group">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-full ${comm.type === 'call' ? 'bg-info/10 text-info' : 'bg-muted'}`}>
                {getIcon(comm.type)}
              </div>
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  {comm.type.charAt(0).toUpperCase() + comm.type.slice(1)}
                  <span className="text-muted-foreground font-normal">
                    {comm.direction === 'inbound' ? 'from' : 'to'}
                  </span>
                  <span className="font-medium text-foreground">
                    {comm.direction === 'inbound'
                      ? (comm.from_number || comm.from_email || (comm.type === 'portal' ? 'the customer portal' : 'Unknown'))
                      : (comm.to_number || comm.to_email || 'Customer')}
                  </span>
                </CardTitle>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {getCallOutcomeBadge(comm)}
                  <span className="text-xs text-muted-foreground">
                    {formatDate(new Date(comm.timestamp), 'MMM d, h:mm a')} - {formatDuration(comm.duration_seconds)}
                  </span>
                  {comm.type === 'call' && comm.tracking_label && (
                    <Badge variant="outline" className="text-xs font-normal" title="CTM tracking label">
                      {comm.tracking_label}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 pt-0 pl-[52px]">
          {/* Content / Subject */}
          {comm.subject && (
            <p className="text-sm font-medium">{comm.subject}</p>
          )}
          {comm.content && (
            <p className="text-sm text-muted-foreground line-clamp-3 group-hover:line-clamp-none transition-all">
              {comm.content}
            </p>
          )}

          {/* Call-specific Actions */}
          {comm.type === 'call' && (
            <div className="flex items-center gap-2 mt-2">
              {comm.recording_available && (
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                  <Play className="h-3 w-3" /> Listen
                </Button>
              )}
            </div>
          )}

          {/* Agent Attribution */}
          {comm.agent_name && getAgentDisplay(comm)}
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Communication History</h3>
          <Skeleton className="h-9 w-40" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Communication History</h3>
          <Badge variant="secondary">{communications.length}</Badge>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Log Communication
        </Button>
      </div>

      <div className="space-y-4">
        {communications.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No communications logged yet. SMS, Emails, Calls, and manual notes will appear here.
            </CardContent>
          </Card>
        ) : (
          communications.map(renderCommunicationCard)
        )}
      </div>

      <AddCommunicationDialog
        customerId={customerId}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
}
